import { Button } from "@nimit9/signet-ui/components/button"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Pagination } from "@nimit9/signet-ui/components/pagination"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router"
import { CheckSquareIcon, PlusIcon } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { z } from "zod"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { ConflictsBanner } from "@/features/transactions/conflict-pick"
import { flattenCategories, useCategories } from "@/hooks/use-categories"
import { useDateFormat } from "@/hooks/use-date-format"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { useDecryptedPersons } from "@/hooks/use-persons"
import { type TransactionFilters, useTransactions } from "@/hooks/use-transactions"
import { api } from "@/lib/api"
import { pickableCategories } from "@/lib/pickable-categories"
import { useSetSharedFile, useSharedFile } from "@/store/selectors"
import { useUIStore } from "@/store/ui"
import { AddTransactionDialog } from "./-add-transaction-dialog"
import { AutoCategorizeDialog } from "./-auto-categorize-dialog"
import { CsvImportDialog } from "./-csv-import-dialog"
import { DeleteConfirmDialog } from "./-delete-confirm-dialog"
import { BulkActionBar, SelectAllBanners, SummaryStrip } from "./-transactions-bulk-bar"
import { TransactionsFilters } from "./-transactions-filters"
import { TransactionsHeaderAction } from "./-transactions-header-action"
import { PageSkeleton, TableSkeleton } from "./-transactions-skeletons"
import { TransactionCards, TransactionsTable } from "./-transactions-table"
import { UploadStatementDialog } from "./-upload-statement-dialog"

const transactionSearchSchema = z.object({
  page: z.number().default(1).catch(1),
  search: z.string().default("").catch(""),
  categoryType: z.string().default("").catch(""),
  owner: z.string().default("").catch(""),
  type: z.string().default("").catch(""),
  categoryId: z.string().default("").catch(""),
  personId: z.string().default("").catch(""),
  startDate: z.string().default("").catch(""),
  endDate: z.string().default("").catch(""),
  amountMin: z.string().default("").catch(""),
  amountMax: z.string().default("").catch(""),
  uncategorized: z.boolean().default(false).catch(false),
})

export const Route = createFileRoute("/transactions/")({
  validateSearch: transactionSearchSchema,
  component: Transactions,
})

const PAGE_SIZE = 30

/** First day of the current month, local time ("YYYY-MM-DD"). */
export function monthStart(today = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`
}

function Transactions() {
  const ownerLabels = useOwnerLabels()
  const formatDate = useDateFormat()
  const openPanel = useUIStore((s) => s.openPanel)
  const activePanel = useUIStore((s) => s.panel)
  const pendingAction = useUIStore((s) => s.pendingAction)
  const setPendingAction = useUIStore((s) => s.setPendingAction)
  const queryClient = useQueryClient()
  const navigate = useNavigate({ from: "/transactions/" })
  // A bare visit (sidebar, tab bar) opens on this month (UAT J6-04). A link that carries any
  // filter (e.g. "45 uncategorised" from Home) keeps all time; "Clear filters" goes back to all time.
  const searchStr = useRouterState({ select: (s) => s.location.searchStr })
  const bareVisit = useRef(searchStr === "")
  useEffect(() => {
    if (!bareVisit.current) return
    bareVisit.current = false
    navigate({ search: (prev) => ({ ...prev, startDate: monthStart() }), replace: true })
  }, [navigate])
  // Phone (< md) swaps the table for date-grouped cards, the filter card for a
  // Drawer, and always-on checkboxes for an explicit Select mode.
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const [selectMode, setSelectMode] = useState(false)

  const {
    page,
    search,
    categoryType,
    owner,
    type,
    categoryId,
    personId,
    startDate,
    endDate,
    amountMin,
    amountMax,
    uncategorized,
  } = Route.useSearch()

  const [showAdvanced, setShowAdvanced] = useState(
    Boolean(type || categoryId || personId || startDate || endDate || amountMin || amountMax),
  )
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [allPagesSelected, setAllPagesSelected] = useState(false)
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())
  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    count: number
    payload:
      | { ids: string[] }
      | { filters: Record<string, unknown>; excludeIds?: string[]; confirmDeleteAll?: boolean }
  } | null>(null)
  const [csvOpen, setCsvOpen] = useState(false)
  const [autoCategorizeOpen, setAutoCategorizeOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [statementFile, setStatementFile] = useState<File | null>(null)
  const sharedFile = useSharedFile()
  const setSharedFile = useSetSharedFile()
  const [addOpen, setAddOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (pendingAction === "csv_import") {
      setCsvOpen(true)
      setPendingAction(null)
    } else if (pendingAction === "statement_import") {
      // A PDF/XLS shared from the phone's share sheet (UAT J11-01).
      setStatementFile(sharedFile)
      setSharedFile(null)
      setUploadOpen(true)
      setPendingAction(null)
    } else if (pendingAction === "export_transactions") {
      window.open("/api/export?format=csv", "_blank")
      setPendingAction(null)
    } else if (pendingAction === "add_transaction") {
      // Hand-off from the phone tab bar's centre "Add" button (S1).
      setAddOpen(true)
      setPendingAction(null)
    }
  }, [pendingAction, setPendingAction, sharedFile, setSharedFile])

  useEffect(() => {
    setFocusedIndex((current) => (current === -1 ? current : -1))
  }, [])

  useHotkeys("/", () => searchInputRef.current?.focus(), { preventDefault: true })
  useHotkeys("j", () => setFocusedIndex((i) => Math.min(i + 1, PAGE_SIZE - 1)), {
    preventDefault: true,
  })
  useHotkeys("k", () => setFocusedIndex((i) => Math.max(i - 1, 0)), { preventDefault: true })
  useHotkeys("n", () => setAddOpen(true), { preventDefault: true })
  useHotkeys("mod+u", () => setUploadOpen(true), { preventDefault: true })
  useHotkeys("right", () => setPage((p) => Math.min(p + 1, totalPages)), { preventDefault: true })
  useHotkeys("left", () => setPage((p) => Math.max(p - 1, 1)), { preventDefault: true })

  function setSearch(v: string) {
    navigate({ search: (prev) => ({ ...prev, search: v, page: 1 }) })
  }
  function setOwner(v: string) {
    navigate({ search: (prev) => ({ ...prev, owner: v, page: 1 }) })
  }
  function setPage(updater: number | ((p: number) => number)) {
    navigate({
      search: (prev) => ({
        ...prev,
        page: typeof updater === "function" ? updater(prev.page) : updater,
      }),
    })
  }

  const filters: TransactionFilters = {
    page,
    pageSize: PAGE_SIZE,
    ...(search && { search }),
    ...(categoryType && { categoryType }),
    ...(owner && { owner }),
    ...(type && { type }),
    ...(categoryId && { categoryId }),
    ...(personId && { personId }),
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(amountMin && { amountMin: Number(amountMin) }),
    ...(amountMax && { amountMax: Number(amountMax) }),
    ...(uncategorized && { uncategorized: true }),
  }

  const { data, isLoading, isFetching, isPlaceholderData, isError, error, refetch } =
    useTransactions(filters)
  const { data: persons } = useDecryptedPersons()
  const activePerson = personId ? persons?.find((p) => p.id === personId) : undefined
  const { data: catData } = useCategories()
  const { data: dupData } = useQuery({
    queryKey: ["transactions", "duplicates"],
    queryFn: () => api.get<{ count: number }>("/transactions/duplicates"),
    select: (d) => d.count,
  })

  const txns = data?.data || []
  const total = data?.total || 0
  const totalPages = data?.totalPages || 0
  const hasFilters =
    search ||
    categoryType ||
    owner ||
    type ||
    categoryId ||
    personId ||
    startDate ||
    endDate ||
    amountMin ||
    amountMax ||
    uncategorized
  // Everything except search — search stays visible on phone, the rest lives
  // in the filter Drawer whose trigger shows this count.
  const activeFilterCount = [
    categoryType || uncategorized,
    owner,
    type,
    categoryId,
    personId,
    startDate || endDate,
    amountMin || amountMax,
  ].filter(Boolean).length

  const selectedCount = allPagesSelected ? total - excludedIds.size : selectedIds.size
  const allSelected =
    allPagesSelected || (txns.length > 0 && txns.every((t) => selectedIds.has(t.id)))
  const flatCats = catData ? flattenCategories(catData.data) : []

  const categoryGroups = useMemo(
    () =>
      ["needs", "wants", "investments", "income"]
        .map((t) => ({
          value: t.charAt(0).toUpperCase() + t.slice(1),
          items: pickableCategories(flatCats.filter((c) => c.type === t && !c.children?.length)),
        }))
        .filter((g) => g.items.length > 0),
    [flatCats],
  )

  // Totals come from the server summary (full filtered set, every page). Fall back
  // to a page-local reduce only when the summary is unavailable (loading/older API)
  // so the cards never silently report just the visible page.
  const pageReduceSpend = useMemo(
    () => txns.filter((t) => t.type === "debit").reduce((s, t) => s + parseFloat(t.amount), 0),
    [txns],
  )
  const pageReduceIncome = useMemo(
    () => txns.filter((t) => t.type === "credit").reduce((s, t) => s + parseFloat(t.amount), 0),
    [txns],
  )
  const totalSpend = data?.summary?.spent ?? pageReduceSpend
  const totalIncome = data?.summary?.income ?? pageReduceIncome

  const bulkMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch("/transactions/bulk", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transactions"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard"] })
      setSelectedIds(new Set())
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.delete("/transactions/bulk", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transactions"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard"] })
      setSelectedIds(new Set())
      setAllPagesSelected(false)
      setExcludedIds(new Set())
      setDeleteConfirmState(null)
    },
  })

  // First / last row on the page ("g g" is taken by Goals).
  useHotkeys("home", () => setFocusedIndex(0), { preventDefault: true })
  useHotkeys(
    "shift+g",
    () => setFocusedIndex(Math.max(txns.length - 1, 0)),
    { preventDefault: true },
    [txns.length],
  )
  // 1–6: All / Needs / Wants / Invest / Income / Transfer, same order as the filter pills.
  useHotkeys(
    "1,2,3,4,5,6",
    (e) => {
      const types = ["", "needs", "wants", "investments", "income", "transfer"]
      const categoryType = types[Number(e.key) - 1] ?? ""
      navigate({ search: (prev) => ({ ...prev, categoryType, uncategorized: false, page: 1 }) })
    },
    { preventDefault: true },
  )
  useHotkeys(
    "mod+a",
    () => setSelectedIds(new Set(txns.map((t) => t.id))),
    { preventDefault: true },
    [txns],
  )
  useHotkeys(
    "e",
    () => {
      const txn = txns[focusedIndex]
      if (txn) openPanel("transaction", txn as unknown as Record<string, unknown>)
    },
    { preventDefault: true },
    [focusedIndex, txns],
  )
  useHotkeys(
    "i",
    () => {
      const txn = txns[focusedIndex]
      if (txn) bulkMutation.mutate({ ids: [txn.id], isIgnored: !txn.isIgnored })
    },
    { preventDefault: true },
    [focusedIndex, txns],
  )
  useHotkeys(
    "enter",
    () => {
      const txn = txns[focusedIndex]
      if (txn) openPanel("transaction", txn as unknown as Record<string, unknown>)
    },
    { preventDefault: true },
    [focusedIndex, txns],
  )
  useHotkeys(
    "x",
    () => {
      const txn = txns[focusedIndex]
      if (!txn) return
      if (allPagesSelected) {
        setExcludedIds((prev) => {
          const next = new Set(prev)
          next.has(txn.id) ? next.delete(txn.id) : next.add(txn.id)
          return next
        })
      } else {
        setSelectedIds((prev) => {
          const next = new Set(prev)
          next.has(txn.id) ? next.delete(txn.id) : next.add(txn.id)
          return next
        })
      }
    },
    { preventDefault: true },
    [focusedIndex, txns, allPagesSelected],
  )

  function clearFilters() {
    navigate({
      search: {
        page: 1,
        search: "",
        categoryType: "",
        owner: "",
        type: "",
        categoryId: "",
        personId: "",
        startDate: "",
        endDate: "",
        amountMin: "",
        amountMax: "",
        uncategorized: false,
      },
    })
    setShowAdvanced(false)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: any filter change clears the selection; the filters are triggers, not inputs
  useEffect(() => {
    setSelectedIds(new Set())
    setAllPagesSelected(false)
    setExcludedIds(new Set())
  }, [
    search,
    categoryType,
    owner,
    type,
    categoryId,
    personId,
    startDate,
    endDate,
    amountMin,
    amountMax,
    uncategorized,
  ])

  function toggleRow(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    if (allPagesSelected) {
      setExcludedIds((prev) => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
      })
    }
  }

  function toggleAll() {
    if (allPagesSelected) {
      setAllPagesSelected(false)
      setSelectedIds(new Set())
      setExcludedIds(new Set())
    } else if (allSelected) {
      // Remove only the current page's rows — preserve selections from other pages
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const t of txns) next.delete(t.id)
        return next
      })
    } else {
      // Add current page's rows to whatever is already selected
      setSelectedIds((prev) => new Set([...prev, ...txns.map((t) => t.id)]))
    }
  }

  function selectAllPages() {
    setAllPagesSelected(true)
    setExcludedIds(new Set())
  }

  function clearSelection() {
    setSelectedIds(new Set())
    setAllPagesSelected(false)
    setExcludedIds(new Set())
  }

  function toggleSelectMode() {
    if (selectMode) clearSelection()
    setSelectMode((v) => !v)
  }

  const hasSelection = selectedIds.size > 0 || allPagesSelected

  const currentFilters: Record<string, unknown> = {
    ...(search && { search }),
    ...(categoryType && { categoryType }),
    ...(owner && { owner }),
    ...(type && { type }),
    ...(categoryId && { categoryId }),
    ...(personId && { personId }),
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(amountMin && { amountMin: Number(amountMin) }),
    ...(amountMax && { amountMax: Number(amountMax) }),
    ...(uncategorized && { uncategorized: true }),
  }

  function openDeleteConfirm(
    payload:
      | { ids: string[] }
      | { filters: Record<string, unknown>; excludeIds?: string[]; confirmDeleteAll?: boolean },
    count: number,
  ) {
    setDeleteConfirmState({ count, payload })
  }

  function confirmDelete() {
    if (!deleteConfirmState) return
    bulkDeleteMutation.mutate(deleteConfirmState.payload as unknown as Record<string, unknown>)
  }

  const headerAction = (
    <TransactionsHeaderAction
      dupCount={dupData}
      search={search}
      categoryType={categoryType}
      owner={owner}
      type={type}
      categoryId={categoryId}
      startDate={startDate}
      endDate={endDate}
      onUpload={() => setUploadOpen(true)}
      onAutoCategorize={() => setAutoCategorizeOpen(true)}
      onImportCsv={() => setCsvOpen(true)}
    />
  )

  if (isLoading) {
    return (
      <>
        <Header title="Transactions" showPeriod={false} action={headerAction} />
        <PageWrapper>
          <PageSkeleton />
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Transactions" showPeriod={false} action={headerAction} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <Header
        title="Transactions"
        subtitle={total > 0 ? `${total} transactions` : undefined}
        showPeriod={false}
        action={headerAction}
      />
      <PageWrapper>
        <ConflictsBanner />
        <TransactionsFilters
          searchInputRef={searchInputRef}
          search={search}
          startDate={startDate}
          endDate={endDate}
          owner={owner}
          ownerLabels={ownerLabels}
          categoryType={categoryType}
          uncategorized={uncategorized}
          type={type}
          categoryId={categoryId}
          personId={personId}
          persons={persons ?? []}
          amountMin={amountMin}
          amountMax={amountMax}
          flatCats={flatCats}
          showAdvanced={showAdvanced}
          hasFilters={hasFilters}
          setSearch={setSearch}
          setOwner={setOwner}
          setShowAdvanced={setShowAdvanced}
          clearFilters={clearFilters}
          navigateSearch={(updater) => navigate({ search: updater })}
          isDesktop={isDesktop}
          activeFilterCount={activeFilterCount}
          toolbarExtra={
            txns.length > 0 ? (
              <Button
                variant={selectMode ? "secondary" : "outline"}
                size="sm"
                className="h-8 shrink-0"
                onClick={toggleSelectMode}
                aria-pressed={selectMode}
              >
                {selectMode ? (
                  "Done"
                ) : (
                  <>
                    <CheckSquareIcon className="size-3.5" />
                    Select
                  </>
                )}
              </Button>
            ) : null
          }
        />

        <SelectAllBanners
          allPagesSelected={allPagesSelected}
          allSelected={allSelected}
          total={total}
          pageSize={PAGE_SIZE}
          txnsCount={txns.length}
          selectAllPages={selectAllPages}
          clearSelection={allPagesSelected ? clearSelection : () => setSelectedIds(new Set())}
        />

        {/* Active-person banner — appears when filtering by a counterparty */}
        {personId && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-border-subtle bg-bg-surface px-4 py-3">
            <p className="min-w-0 text-body text-text-secondary">
              All transactions with{" "}
              <span className="font-medium text-text-primary">
                {activePerson?.name ?? "this person"}
              </span>{" "}
              — spent <Amount value={totalSpend ? -totalSpend : 0} />, received{" "}
              <Amount value={totalIncome} showSign={totalIncome > 0} />
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="text-text-muted"
              onClick={() => navigate({ search: (prev) => ({ ...prev, personId: "", page: 1 }) })}
            >
              Clear
            </Button>
          </div>
        )}

        {/* Summary strip — stays visible while paginating/filtering (placeholder data) */}
        {txns.length > 0 && (
          // #58: spent/income aggregate the full filtered set (server summary), not the visible page
          <SummaryStrip
            spent={totalSpend}
            income={totalIncome}
            total={total}
            period={
              startDate || endDate
                ? `${startDate ? formatDate(startDate) : "Start"} – ${endDate ? formatDate(endDate) : "today"}`
                : "All time"
            }
          />
        )}

        {/* Bulk action bar */}
        {hasSelection && (
          <BulkActionBar
            isDesktop={isDesktop}
            selectedCount={selectedCount}
            selectedIds={selectedIds}
            allPagesSelected={allPagesSelected}
            txns={txns}
            categoryGroups={categoryGroups}
            bulkPending={bulkMutation.isPending}
            bulkDeletePending={bulkDeleteMutation.isPending}
            onSetIgnored={(ids, isIgnored) => bulkMutation.mutate({ ids, isIgnored })}
            onSetTransfer={(ids) => bulkMutation.mutate({ ids, isTransfer: true })}
            onSetOwner={(ids, owner) => bulkMutation.mutate({ ids, owner })}
            onSetCategory={(ids, categoryIdToSet) =>
              bulkMutation.mutate({ ids, categoryId: categoryIdToSet })
            }
            onDelete={() => {
              if (allPagesSelected) {
                openDeleteConfirm(
                  {
                    filters: currentFilters,
                    // Server requires explicit opt-in when no filter is active (#132);
                    // the user already confirmed the count in the dialog.
                    confirmDeleteAll: true,
                    ...(excludedIds.size > 0 && { excludeIds: Array.from(excludedIds) }),
                  },
                  selectedCount,
                )
              } else {
                openDeleteConfirm({ ids: Array.from(selectedIds) }, selectedIds.size)
              }
            }}
            onClear={clearSelection}
          />
        )}

        {/* Error */}
        {isError && <ErrorState message={(error as Error)?.message} onRetry={refetch} />}

        {/* Empty — only show once fetch is settled with no results */}
        {!isLoading && !isFetching && txns.length === 0 && (
          <EmptyState
            title={hasFilters ? "No matching transactions" : "No transactions yet"}
            description={
              hasFilters
                ? "Try adjusting your filters"
                : "Upload a statement, import a CSV, or add one by hand. Your AI assistant can also bring them in for you."
            }
            action={
              hasFilters ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button variant="default" size="sm" onClick={() => setUploadOpen(true)}>
                  Upload a statement
                </Button>
              )
            }
          />
        )}

        {/* Transaction list */}
        {!isLoading && txns.length > 0 && isDesktop && (
          // No overflow-hidden here: it would turn this card into the sticky
          // header's scroll container and stop the header from pinning.
          <div className="rounded-xl border border-border-subtle bg-bg-surface">
            {isFetching && isPlaceholderData ? (
              <TableSkeleton />
            ) : (
              <TransactionsTable
                txns={txns}
                selectedIds={selectedIds}
                allPagesSelected={allPagesSelected}
                excludedIds={excludedIds}
                focusedIndex={focusedIndex}
                allSelected={allSelected}
                activePanel={activePanel}
                formatDate={formatDate}
                toggleAll={toggleAll}
                toggleRow={toggleRow}
                onRowClick={(txn, idx) => {
                  setFocusedIndex(idx)
                  openPanel("transaction", { id: txn.id, transaction: txn })
                }}
                onDelete={(id) => openDeleteConfirm({ ids: [id] }, 1)}
              />
            )}

            {/* Pagination footer — right padding clears the desktop FAB */}
            {totalPages > 1 && !isPlaceholderData && (
              <div className="flex items-center justify-between border-t border-border-subtle px-5 py-3 pr-20">
                <p className="text-caption text-text-muted">
                  Page {page} of {totalPages}
                </p>
                <Pagination page={page} totalPages={totalPages} onPageChange={(p) => setPage(p)} />
              </div>
            )}
          </div>
        )}

        {!isLoading && txns.length > 0 && !isDesktop && (
          <>
            {selectMode && (
              <div className="mb-2 flex items-center justify-between">
                <Button variant="link" size="sm" className="h-auto px-0" onClick={toggleAll}>
                  {allSelected ? "Deselect all" : "Select all on page"}
                </Button>
                <span className="text-caption text-text-muted">Tap rows to select</span>
              </div>
            )}
            {isFetching && isPlaceholderData ? (
              <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
                <TableSkeleton />
              </div>
            ) : (
              <TransactionCards
                txns={txns}
                selectMode={selectMode}
                selectedIds={selectedIds}
                allPagesSelected={allPagesSelected}
                excludedIds={excludedIds}
                activePanel={activePanel}
                ownerLabels={ownerLabels}
                formatDate={formatDate}
                toggleRow={toggleRow}
                onRowClick={(txn, idx) => {
                  setFocusedIndex(idx)
                  openPanel("transaction", { id: txn.id, transaction: txn })
                }}
              />
            )}
            {totalPages > 1 && !isPlaceholderData && (
              <div className="mt-3 flex items-center justify-between">
                <p className="text-caption text-text-muted">
                  Page {page} of {totalPages}
                </p>
                <Pagination page={page} totalPages={totalPages} onPageChange={(p) => setPage(p)} />
              </div>
            )}
            {/* Keeps the last rows reachable above the fixed bulk bar. */}
            {hasSelection && <div aria-hidden className="h-28" />}
          </>
        )}
      </PageWrapper>

      <CsvImportDialog open={csvOpen} onClose={() => setCsvOpen(false)} />
      <AutoCategorizeDialog
        open={autoCategorizeOpen}
        onClose={() => setAutoCategorizeOpen(false)}
      />
      <UploadStatementDialog
        open={uploadOpen}
        initialFile={statementFile}
        onManualAdd={() => setAddOpen(true)}
        onClose={() => {
          setUploadOpen(false)
          setStatementFile(null)
        }}
      />
      <AddTransactionDialog open={addOpen} onClose={() => setAddOpen(false)} />

      <DeleteConfirmDialog
        open={deleteConfirmState !== null}
        count={deleteConfirmState?.count ?? 0}
        isPending={bulkDeleteMutation.isPending}
        onCancel={() => setDeleteConfirmState(null)}
        onConfirm={confirmDelete}
      />

      {/* FAB — desktop only. On phone the tab bar's centre Add button is
          this page's quick-add entry point; keeping this one too would
          duplicate it (S1/S3 — see components/layout/tab-bar.tsx). */}
      <Button
        onClick={() => setAddOpen(true)}
        className="fixed bottom-6 right-6 z-50 hidden h-12 w-12 rounded-full shadow-lg shadow-accent-green/20 transition-transform hover:scale-105 active:scale-95 md:flex"
        size="icon"
        aria-label="Add transaction (desktop)"
      >
        <PlusIcon className="h-5 w-5" />
      </Button>
    </>
  )
}
